import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { Database, EnneagramTypeId } from "@/types/supabase";
import type { ClassifyResult, ExpressAnswers } from "../express/types";

export type DecodificarExpressRow =
  Database["public"]["Tables"]["decodificar_express"]["Row"];
export type DecodificarExpressInsert =
  Database["public"]["Tables"]["decodificar_express"]["Insert"];

export interface SaveExpressInput {
  patientLabel?: string | null;
  freeNote?: string | null;
  signals: ExpressAnswers;
  result: ClassifyResult;
}

function breakdownToJson(breakdown: ClassifyResult["breakdown"]) {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(breakdown)) out[k] = v;
  return out;
}

export async function saveExpress(
  input: SaveExpressInput,
  client: SupabaseClient = supabase,
): Promise<DecodificarExpressRow> {
  const { data, error } = await client
    .from("decodificar_express")
    .insert({
      patient_label: input.patientLabel ?? null,
      free_note: input.freeNote ?? null,
      signals: input.signals as DecodificarExpressInsert["signals"],
      result_type: input.result.primary,
      secondary_type: input.result.secondary,
      confidence: input.result.confidence,
      ambiguous: input.result.ambiguous,
      breakdown: breakdownToJson(input.result.breakdown),
    })
    .select()
    .single();

  if (error) throw error;
  if (!data) throw new Error("saveExpress: insert returned no row.");
  return data as DecodificarExpressRow;
}

export interface ListExpressParams {
  limit?: number;
}

export async function listExpress(
  params: ListExpressParams = {},
  client: SupabaseClient = supabase,
): Promise<DecodificarExpressRow[]> {
  const { limit = 30 } = params;
  const { data, error } = await client
    .from("decodificar_express")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as DecodificarExpressRow[];
}

export async function deleteExpress(
  id: string,
  client: SupabaseClient = supabase,
): Promise<void> {
  const { error } = await client.from("decodificar_express").delete().eq("id", id);
  if (error) throw error;
}

export async function updateExpress(
  id: string,
  input: SaveExpressInput,
  client: SupabaseClient = supabase,
): Promise<DecodificarExpressRow> {
  const { data, error } = await client
    .from("decodificar_express")
    .update({
      patient_label: input.patientLabel ?? null,
      free_note: input.freeNote ?? null,
      signals: input.signals as DecodificarExpressInsert["signals"],
      result_type: input.result.primary,
      secondary_type: input.result.secondary,
      confidence: input.result.confidence,
      ambiguous: input.result.ambiguous,
      breakdown: breakdownToJson(input.result.breakdown),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  if (!data) throw new Error("updateExpress: update returned no row.");
  return data as DecodificarExpressRow;
}

export async function renameExpress(
  id: string,
  patientLabel: string,
  client: SupabaseClient = supabase,
): Promise<DecodificarExpressRow> {
  const { data, error } = await client
    .from("decodificar_express")
    .update({ patient_label: patientLabel })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  if (!data) throw new Error("renameExpress: update returned no row.");
  return data as DecodificarExpressRow;
}

export type { ClassifyResult, ExpressAnswers };
export type { EnneagramTypeId };
